import { NavLink, Outlet } from "react-router-dom";
import {
  Home,
  Users,
  ClipboardList,
  PhoneCall,
  MapPin,
  Loader2,
  AlertTriangle,
  MessageCircle,
  Package,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ensurePushSubscribed } from "@/lib/pushNotifications";
import LiveCallBar from "@/components/LiveCallBar";

const BASE_NAV = [
  { to: "/app", icon: Home, label: "Home", end: true, id: "emp-nav-home" },
  { to: "/app/customers", icon: Users, label: "Customers", id: "emp-nav-customers" },
  { to: "/app/tasks", icon: ClipboardList, label: "Tasks", id: "emp-nav-tasks" },
  { to: "/app/calls", icon: PhoneCall, label: "Calls", id: "emp-nav-calls" },
];
const CHAT_NAV = {
  to: "/app/chat",
  icon: MessageCircle,
  label: "Chat",
  id: "emp-nav-chat",
};
// Optional 5th tab shown only when the owner has granted this employee
// permission to manage the catalog. Tapping it opens the catalog console
// (owner sidebar filtered down to Marketplace / Orders / Shipping) with a
// "Back to my tasks" link at the bottom.
const CATALOG_NAV = {
  to: "/dashboard/marketplace",
  icon: Package,
  label: "Catalog",
  id: "emp-nav-catalog",
};

const REFRESH_MS = 60_000; // send location every 60s while sharing is on


// Distance in metres between two points.
function haversineM(lat1, lng1, lat2, lng2) {
  const r = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const a =
    Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

export default function EmployeeLayout() {
  const { user } = useAuth();
  const [chatAccess, setChatAccess] = useState(false);

  // Subscribe this device to Web Push once per sign-in so a newly assigned
  // task rings the phone (default notification sound + vibration) even
  // when the app is closed — same "always on while signed in" spirit as
  // GPS sharing below. Best-effort: no-ops silently if unsupported or the
  // employee has denied notification permission.
  useEffect(() => {
    ensurePushSubscribed();
  }, []);

  // Check whether this employee has been added to the team chat, so we
  // conditionally show the Chat tab in the bottom nav.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/chat/access");
        if (!cancelled) setChatAccess(!!data?.has_access);
      } catch (_) {
        if (!cancelled) setChatAccess(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const NAV = [
    ...BASE_NAV,
    ...(chatAccess ? [CHAT_NAV] : []),
    ...(user?.can_manage_marketplace ? [CATALOG_NAV] : []),
  ];

  // Hide the fixed-bottom "Made with Emergent" badge only on the employee
  // mobile app because it overlaps the bottom navigation bar. The badge has
  // inline `!important` styles so CSS can't override it — we mutate the
  // element's inline style directly, and use a MutationObserver in case the
  // element is added later.
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.style.setProperty("display", "none", "important");
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => {
      observer.disconnect();
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.style.removeProperty("display");
    };
  }, []);

  // GPS is ALWAYS ON while an employee is signed in. There is no toggle —
  // once the browser permission is granted the tracking runs for as long as
  // the app is open (and the interval loop keeps pushing fresh fixes).
  const sharing = true;
  const [status, setStatus] = useState("idle"); // idle|locating|ok|error
  const [lastFix, setLastFix] = useState(null); // {lat,lng,ts}
  const [errText, setErrText] = useState("");
  const timerRef = useRef(null);
  const watchRef = useRef(null);

  const lastSentRef = useRef(null); // {lat,lng,ts} of the last fix sent

  const pushLocation = useCallback(async (lat, lng, coords = {}) => {
    // The GPS watcher fires several times a second: send at most one fix
    // every 15 s, unless the person has moved 30 m or more since the last.
    const last = lastSentRef.current;
    if (last && Date.now() - last.ts < 15_000) {
      const moved = haversineM(last.lat, last.lng, lat, lng);
      if (moved < 30) return;
    }
    lastSentRef.current = { lat, lng, ts: Date.now() };
    try {
      await api.post("/locations", {
        lat,
        lng,
        accuracy: coords.accuracy || null,
        speed: coords.speed || null,
        heading: coords.heading || null,
        address: `From device`,
      });
      setLastFix({ lat, lng, ts: Date.now() });
      setStatus("ok");
      setErrText("");
    } catch (e) {
      setStatus("error");
      setErrText("Could not save location to server.");
    }
  }, []);

  const explainGeoError = (err) => {
    switch (err?.code) {
      case 1:
        return "Location permission denied. Please allow location access in your browser settings.";
      case 2:
        return "Location unavailable. Check that GPS/Location Services is turned on.";
      case 3:
        return "Location request timed out. Try again outdoors or on Wi-Fi.";
      default:
        return err?.message || "Could not read your location.";
    }
  };

  const captureOnce = useCallback(
    (announce = false) => {
      if (!("geolocation" in navigator)) {
        setStatus("error");
        setErrText("This browser does not support geolocation.");
        if (announce) toast.error("This browser does not support GPS.");
        return;
      }
      setStatus("locating");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          pushLocation(pos.coords.latitude, pos.coords.longitude, pos.coords);
          if (announce) toast.success("Location updated");
        },
        (err) => {
          setStatus("error");
          const msg = explainGeoError(err);
          setErrText(msg);
          if (announce) toast.error(msg);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    },
    [pushLocation]
  );

  // Kick off / stop sharing loop
  useEffect(() => {
    if (!sharing) {
      setStatus("idle");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (watchRef.current !== null && "geolocation" in navigator) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      return;
    }
    captureOnce(false);
    timerRef.current = setInterval(() => captureOnce(false), REFRESH_MS);
    // Also install a watcher so we react to significant device movement.
    if ("geolocation" in navigator) {
      try {
        watchRef.current = navigator.geolocation.watchPosition(
          (pos) => pushLocation(pos.coords.latitude, pos.coords.longitude, pos.coords),
          () => {
            /* ignore watch errors — captureOnce loop surfaces them */
          },
          { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 }
        );
      } catch (e) {
        /* ignore */
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (watchRef.current !== null && "geolocation" in navigator) {
        navigator.geolocation.clearWatch(watchRef.current);
      }
      timerRef.current = null;
      watchRef.current = null;
    };
  }, [sharing, captureOnce, pushLocation]);

  const dot =
    status === "ok"
      ? "bg-emerald-500"
      : status === "locating"
        ? "bg-amber-500 animate-pulse"
        : status === "error"
          ? "bg-red-500"
          : "bg-slate-300";

  return (
    <div className="min-h-screen bg-[var(--app-bg)] flex flex-col">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#0055FF] text-white grid place-items-center font-heading font-bold">
            {(user?.name || "?").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="mono text-[10px] text-slate-500 truncate">
              {user?.department} · {user?.email}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-1.5"
              data-testid="emp-share-location"
              title="GPS is always on while you are signed in"
            >
              <MapPin
                className={`w-4 h-4 ${status === "ok" ? "text-emerald-500" : status === "error" ? "text-red-500" : "text-[#FF5000]"}`}
              />
              <span className="mono text-[10px] uppercase tracking-widest text-slate-600">
                GPS on
              </span>
            </div>
          </div>
        </div>
        {sharing && (
          <div
            className="max-w-md mx-auto px-4 pb-2 flex items-center gap-2 text-[11px]"
            data-testid="emp-location-status"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {status === "ok" && lastFix && (
              <span className="mono text-slate-500 flex-1 truncate">
                Live: {lastFix.lat.toFixed(4)}, {lastFix.lng.toFixed(4)} · updated{" "}
                {Math.max(0, Math.round((Date.now() - lastFix.ts) / 1000))}s ago
              </span>
            )}
            {status === "locating" && (
              <span className="mono text-slate-500 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Getting your location…
              </span>
            )}
            {status === "error" && (
              <span
                className="mono text-red-600 flex-1 flex items-center gap-1"
                data-testid="emp-location-error"
              >
                <AlertTriangle className="w-3 h-3" /> {errText}
              </span>
            )}
            <button
              type="button"
              onClick={() => captureOnce(true)}
              className="mono text-[10px] uppercase tracking-widest text-[#0055FF] hover:underline"
              data-testid="emp-location-refresh"
            >
              Refresh
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-md mx-auto w-full pb-24">
        <LiveCallBar />
        <Outlet />
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t">
        <div
          className="max-w-md mx-auto grid"
          style={{ gridTemplateColumns: `repeat(${NAV.length}, minmax(0, 1fr))` }}
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-0.5 text-[11px] ${
                  isActive ? "text-[#0055FF]" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon className={`w-5 h-5 ${isActive ? "text-[#0055FF]" : ""}`} />
                  <span>{n.label}</span>
                  {isActive && (
                    <span className="w-1 h-1 rounded-full bg-[#0055FF] mt-0.5" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
